/**
 * Landmark asset substitution table + placement maths (issue #124).
 *
 * A handful of buildings in the AOI are disproportionately recognisable (the
 * All Saints bell-turret; the village school; the parish hall; the Old Chapel;
 * Low Shaw farmstead) and justify a code-authored low-poly glTF asset rendered
 * IN PLACE OF the procedural massing. This module is the pure, three.js-free half: the table
 * keyed by OSM way id, and the deterministic placement (footprint centroid +
 * long-axis orientation) an asset is seated with. The three.js
 * loader/substitution wrapper lives in landmarksLayer.ts.
 *
 * Scene furniture only (hard constraint #1): a landmark asset is decoration over
 * a real footprint and never influences any LOS result — nothing here imports
 * from raypath/ (guarded by buildings.guard.test.ts). Ground elevation still
 * comes from the DTM collider (#6). A missing/failed/absent asset is a reported
 * blocker that falls back to the procedural building, never fabricated data (#7).
 */
import { bngToLocal, type LocalXZ } from '../core/coordinates';

export interface LandmarkEntry {
  /** OSM way id, keyed to a building `id` in data/config/buildings.json. */
  wayId: string;
  /** URL of the committed glTF asset, or null when no asset exists yet — a null
   * asset is the signal to render the footprint procedurally. Both in-scope
   * footprints now ship an asset (All Saints #124, mission school #169); the
   * nullable type is retained so the loader's procedural-fallback path stays
   * exercisable and a future assetless entry needs no type change. */
  asset: string | null;
  /** Human label used in structured warnings. */
  label: string;
  /**
   * Optional per-landmark override (issue #160) flipping the asset's seated
   * end 180deg from the computed `longAxisLocal` direction. `longAxisLocal`
   * canonicalises a line to one of two collinear directions with no notion of
   * which end is the asset's "front" — for a footprint like a church nave that
   * ambiguity can seat the tower on the wrong gable. `flip` corrects that per
   * asset without touching the canonical long-axis derivation (which other
   * consumers, e.g. buildings.ts, rely on being winding-independent). Absent
   * (or false) is the pre-#160 behaviour, unchanged.
   */
  flip?: boolean;
}

/** Static base the viewer serves committed landmark assets from. `viewer/public/`
 * is served at the site root by Vite and is git-tracked (only `public/data/`,
 * pipeline output, is gitignored), so an asset committed under
 * `viewer/public/landmarks/` ships with the build and is fetched from here. */
export const LANDMARK_ASSET_BASE = '/landmarks';

/**
 * The landmark table: the recognisable footprints that have a hand-authored
 * asset rendered in place of the procedural massing. Four cluster in the same
 * village inside the core AOI — All Saints church (#124), the mission school
 * (#169), Corsenside Parish Hall and the Old Chapel (#169 follow-up) — plus Low
 * Shaw farmstead (#124), a stone range at the scene's local origin.
 *
 * There is NO cap on how many landmarks this holds, and no prior "two landmark"
 * limit ever applied — the table is meant to grow. Every extra recognisable
 * landmark is a win: it means more real, place-able geometry that residents
 * actually recognise. The only bar to adding a row is quality — a photo-grounded
 * asset that reads as the real building — never a count.
 *
 * Footprints were confirmed from the reference photos' EXIF GPS. That is also how
 * the mission school landed on `way/362268895` (beside All Saints) rather than the
 * way id first registered for it, `way/233105387` — an unrelated OSM landuse
 * polygon ~7 km outside the core AOI, which was dropped, not kept as an anchor —
 * and how Low Shaw was matched to `way/362268910`.
 */
export const LANDMARK_TABLE: readonly LandmarkEntry[] = [
  // flip: true — the real nave's long axis is ~east-west and longAxisLocal's
  // canonical direction lands the asset's turret gable on the east end, but
  // the tower is the west gable (issue #160, owner-observed on the live build).
  { wayId: 'way/362268897', asset: `${LANDMARK_ASSET_BASE}/all-saints.glb`, label: 'All Saints church', flip: true },
  { wayId: 'way/362268895', asset: `${LANDMARK_ASSET_BASE}/mission-school.glb`, label: 'Mission School' },
  { wayId: 'way/362234674', asset: `${LANDMARK_ASSET_BASE}/village-hall.glb`, label: 'Corsenside Parish Hall' },
  { wayId: 'way/362234709', asset: `${LANDMARK_ASSET_BASE}/old-chapel.glb`, label: 'The Old Chapel' },
  // Low Shaw farmstead — a linear Northumbrian stone range at the scene's local
  // origin, footprint identified from the reference photos' GPS. The asset's
  // doored main front is authored on its local -z face and the rear catslide
  // outshut on +z. flip: true — owner ground truth is that the doored front
  // faces SOUTH (toward High Shaw) and the outshut/extension faces NORTH (toward
  // the quarry); the un-flipped seating faces the doors north, so the 180deg
  // flip is needed to seat them south.
  { wayId: 'way/362268910', asset: `${LANDMARK_ASSET_BASE}/low-shaw.glb`, label: 'Low Shaw farmstead', flip: true },
];

export type Compass = 'N' | 'E' | 'S' | 'W';

export interface LandmarkPlacement {
  /** local x/z of the footprint's vertex-average centroid, to translate the asset to */
  centroid: LocalXZ;
  /** yaw about +Y (radians) rotating the asset's local +x long-axis onto the footprint long-axis */
  yawRad: number;
  /** compass end the asset's local +x ("front"/turret) end lands on — deterministic + documented */
  frontCompass: Compass;
}

/** BNG footprint ring -> local viewer x/z (via the single-source bngToLocal). */
export function footprintToLocalXZ(ringBng: readonly [number, number][]): LocalXZ[] {
  return ringBng.map(([e, n]) => bngToLocal(e, n));
}

/** Vertex-average centroid of a footprint ring — a fine seat point for a small building footprint (not a true area centroid). */
export function footprintCentroidLocal(ringLocal: readonly LocalXZ[]): LocalXZ {
  let sx = 0;
  let sz = 0;
  for (const p of ringLocal) {
    sx += p.x;
    sz += p.z;
  }
  return { x: sx / ringLocal.length, z: sz / ringLocal.length };
}

/**
 * The footprint's long-axis unit direction in local x/z: the direction of the
 * longest edge of the closed ring (ties keep the first-encountered longest edge
 * — deterministic, matching ridgeAxis/primaryFacadeEdge in buildings.ts).
 *
 * A long axis is a line, not a ray, so it is canonicalised to ONE deterministic
 * direction out of the two collinear choices: pointing +east (dir.x > 0), or
 * +north (local -z) when the axis is (near) north-south. That makes the seated
 * orientation reproducible and the documented "front" gable stable. Null for a
 * degenerate ring (<2 points, or a zero-length longest edge).
 */
export function longAxisLocal(ringLocal: readonly LocalXZ[]): { x: number; z: number } | null {
  const n = ringLocal.length;
  if (n < 2) return null;
  let bx = 0;
  let bz = 0;
  let best = 0;
  for (let i = 0; i < n; i++) {
    const a = ringLocal[i];
    const b = ringLocal[(i + 1) % n];
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const lenSq = dx * dx + dz * dz;
    if (lenSq > best) {
      best = lenSq;
      bx = dx;
      bz = dz;
    }
  }
  if (!(best > 0)) return null;
  const len = Math.sqrt(best);
  let dx = bx / len;
  let dz = bz / len;
  // Canonical ray: +east wins; on a (near) N-S axis, +north (local -z) wins.
  const EPS = 1e-9;
  if (dx < -EPS || (Math.abs(dx) <= EPS && dz > 0)) {
    dx = -dx;
    dz = -dz;
  }
  return { x: dx, z: dz };
}

/** Compass end a canonical local direction points at. Local axes (coordinates.ts):
 * +x = east, -x = west, -z = north, +z = south. */
function compassOf(dir: { x: number; z: number }): Compass {
  if (Math.abs(dir.x) >= Math.abs(dir.z)) return dir.x >= 0 ? 'E' : 'W';
  return dir.z < 0 ? 'N' : 'S';
}

/**
 * Placement for seating a landmark asset over a footprint: the centroid to
 * translate to, the yaw that rotates the asset's local +x long-axis onto the
 * footprint's long axis, and the compass end the asset's +x ("front") lands on.
 * Null for a degenerate footprint (<3 points), so the caller falls back
 * procedurally rather than seating a guess.
 *
 * The asset is authored with its base at y=0 and long axis +x. A three.js
 * rotateY(yaw) sends local +x (1,0,0) to (cos yaw, 0, -sin yaw); solving that
 * equals the footprint long-axis (dir.x, 0, dir.z) gives yaw = atan2(-dir.z, dir.x).
 * Vertical seating (y) is the terrain collider's job (hard constraint #6), not
 * this pure module's.
 *
 * `options.flip` (issue #160) rotates the seated asset 180deg by negating the
 * canonical long-axis direction before computing yaw/frontCompass, correcting
 * the arbitrary end `longAxisLocal` happened to canonicalise to. It is a
 * per-landmark override (see `LandmarkEntry.flip`), not a general "front
 * points west" rule — future non-church landmarks may need neither end.
 */
export function landmarkPlacement(
  ringLocal: readonly LocalXZ[],
  options?: { flip?: boolean },
): LandmarkPlacement | null {
  if (ringLocal.length < 3) return null;
  const dir = longAxisLocal(ringLocal);
  if (!dir) return null;
  const seatedDir = options?.flip ? { x: -dir.x, z: -dir.z } : dir;
  return {
    centroid: footprintCentroidLocal(ringLocal),
    yawRad: Math.atan2(-seatedDir.z, seatedDir.x),
    frontCompass: compassOf(seatedDir),
  };
}
